<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <div class="sidebar-logo">
      <img :src="menuLogoSrc" alt="JTP" />
    </div>

    <VaInput v-model="filter" :placeholder="t('general.search')" class="w-full" clearable>
      <template #prependInner>
        <VaIconMagnifier />
      </template>
    </VaInput>

    <!-- Favorite menu items -->
    <VaAccordion v-show="favoriteMenuItem.children?.length" v-model="valueFavorite" multiple>
      <VaCollapse :key="favoriteMenuItem.id * -1">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="favoriteMenuItem.children ? undefined : { name: favoriteMenuItem.route }"
            :aria-label="`${favoriteMenuItem.children ? 'Open category ' : 'Visit'} ${t(
              favoriteMenuItem.displayNameKey as string,
            )}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-1 pr-2 pl-4">
              <VaIcon
                v-if="favoriteMenuItem.icon"
                aria-hidden="true"
                :name="favoriteMenuItem.icon"
                size="20px"
                color="secondary"
              />
              <VaSidebarItemTitle
                class="flex justify-between items-center leading-5 font-semibold text-wrap"
                color="secondary"
              >
                {{ t(favoriteMenuItem.displayNameKey as string) }}
                <VaIcon v-if="favoriteMenuItem.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="childRoute in favoriteMenuItem.children" :key="childRoute.id">
            <SidebarLinkItem
              :item="childRoute"
              :aria-label="`Visit ${t(childRoute.displayNameKey as string)}`"
              icon-color="secondary"
              favorite-color="secondary"
              text-color="secondary"
              :level="2"
              content-class="py-1 pr-2"
              title-class="leading-5 font-semibold text-wrap"
              :handle-favorite="handleFavorite"
            />
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>

    <!-- Menu items -->
    <SidebarMenuItem
      v-for="routeItem in menuHierarchyDisplay"
      :key="routeItem.id"
      :item="routeItem"
      :active-color="activeColor"
      :icon-color="iconColor"
      :text-color="textColor"
      :arrow-direction="arrowDirection"
      :handle-favorite="handleFavorite"
      :level="0"
      :force-expand="forceExpand"
    />
  </VaSidebar>
</template>

<script setup lang="ts">
import { watch, ref, computed, onMounted, inject } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useCommon } from "@/pages/common/composables/useCommon"
import { useReport } from "@/pages/report/viewer/composables/useReport"
import type { HelpMenuItem } from "@/pages/admin/help/types"
import { useGlobalStore } from "@/stores/global-store"
import { useUserStore } from "@/stores/user-store"
import { useHelpStore } from "@/stores/help-store"
import { useI18n } from "vue-i18n"
import { useColors } from "vuestic-ui"
import { canAccessRoute } from "@/services/auth"
import { MenuHierarchy } from "@/pages/admin/menuHierarchy/types"
import { storeToRefs } from "pinia"
import { canAccess } from "@/services/authorizationPlugin"
import { PermissionAction } from "@shared/components/core/helpers/securityTypes"
import { isExternalRoute, getTranslatedLabel } from "@shared/helpers/utils"
import SidebarMenuItem from "./components/SidebarMenuItem.vue"
import SidebarLinkItem from "./components/SidebarLinkItem.vue"
import VaIconMagnifier from "@shared/components/icons/VaIconMagnifier.vue"

// Props
interface Props {
  visible?: boolean
  mobile?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  visible: true,
  mobile: false,
})

// Emits
const emit = defineEmits<{
  "update:visible": [value: boolean]
}>()

// Composables
const { getColor, colorToRgba } = useColors()
const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const globalStore = useGlobalStore()
const userStore = useUserStore()
const { userPreferences } = storeToRefs(globalStore)
const { user } = storeToRefs(userStore)
const { getSettings, saveSettings, getMenuHierarchy, loadAppCustomizations } = useCommon()
const { getReports } = useReport()
const helpStore = useHelpStore()

const config = inject("$config") as any
const legacyJptUrl = config?.legacyJptUrl || ""

// Reactive data
const allRoutes = router.getRoutes()
const favoriteMenuItem = ref<MenuHierarchy>({
  id: -1,
  displayNameKey: "general.favorites",
  icon: "favorite",
  isActive: false,
  isFavorite: false,
  children: [],
  displayOrder: -1,
})
const reportsMenuItem = ref<MenuHierarchy>({
  id: -2,
  displayNameKey: "general.reports",
  icon: "assignment",
  isActive: true,
  isFavorite: false,
  children: [],
  displayOrder: 9999,
})
let rawMenuHierarchy: MenuHierarchy[] = []
const menuHierarchy = ref<MenuHierarchy[]>([])
const menuHierarchyDisplay = ref<MenuHierarchy[]>([])
const filter = ref<string>("")
const forceExpand = ref(false)
let fetchedHelps: HelpMenuItem[] = []
const value = ref<boolean[]>([])
const valueFavorite = ref<boolean[]>([])
let reportParentCount = 1000

// Computed
const menuLogoSrc = computed<string>(() => {
  return (
    globalStore.getCustomLogoUrl("Logo.Menu", locale.value) ??
    globalStore.getCustomLogoUrl("Logo.Menu", "") ??
    "/images/jtp-logo.png"
  )
})

const writableVisible = computed({
  get: () => props.visible,
  set: (v: boolean) => emit("update:visible", v),
})

const sidebarWidth = computed(() => (props.mobile ? "100vw" : "350px"))
const color = computed(() => getColor("background-secondary"))
const activeColor = computed(() => colorToRgba(getColor("focus"), 0.3))

const routeHasActiveChild = (section: MenuHierarchy): boolean => {
  if (!section.children && route.name) {
    return isActiveRoute(section)
  } else if (section.children && route.name) {
    return section.children.some((item) => isActiveRoute(item))
  }
  return false
}

const isActiveRoute = (child: MenuHierarchy): boolean => {
  if (!child.route) return false
  if (child.route === "help-display") {
    const paramJson = child.routeParam ? JSON.parse(child.routeParam) : {}
    return route.params.uniqueId == paramJson.uniqueId
  }
  if (child.route === "report-viewer") {
    const paramJson = child.routeParam ? JSON.parse(child.routeParam) : {}
    return route.params.id == paramJson.id
  }
  return route!.name!.toString().endsWith(`${child.route}`)
}

const setActiveExpand = () => {
  value.value = menuHierarchy.value.map((route: MenuHierarchy) => routeHasActiveChild(route))!
}

const iconColor = (route: MenuHierarchy) => (routeHasActiveChild(route) ? "primary" : "secondary")
const textColor = (route: MenuHierarchy) => (routeHasActiveChild(route) ? "primary" : "textPrimary")
const arrowDirection = (state: boolean) => (state ? "va-arrow-up" : "va-arrow-down")

async function loadMenu() {
  rawMenuHierarchy = await getMenuHierarchy()

  handleMenuPermissions()
  handleMenuHierarchy(null)
  handleFavoriteMenu()
  handleReportsMenu()
  await handleHelpMenu()
  setActiveExpand()
}

const replaceExternalRoute = (route: string): string => {
  if (route.startsWith("{legacyJptUrl}/")) return route.replace("{legacyJptUrl}", legacyJptUrl)
  return route
}

function handleMenuPermissions() {
  const rawMenu = [...rawMenuHierarchy]

  rawMenu.forEach((item) => {
    if (!item.isActive) return
    if (!item.route) return

    // For external routes, we check permissions based on the route name before replacing it with the actual URL. If the user does not have access, we mark the item as inactive. If they do have access, we replace the route with the actual URL.
    if (isExternalRoute(item.route)) {
      if (!canAccessRoute(item.route)) item.isActive = false
      else item.route = replaceExternalRoute(item.route)
      return
    }

    const matchedRoute = allRoutes.find((r) => r.name === item.route)
    if (!matchedRoute || !canAccessRoute(matchedRoute.name as string)) item.isActive = false
  })
  menuHierarchy.value = rawMenu

  // Check if user has access to the current route. If not, redirect to 404
  if (route.name && !canAccessRoute(route.name.toString())) {
    router.push({ name: "401" })
  }
}

function handleMenuHierarchy(parent: MenuHierarchy | null) {
  let parents: MenuHierarchy[] | null = null
  const favoriteIds = userPreferences.value.menuFavorites ? userPreferences.value.menuFavorites.split(",") : []
  menuHierarchy.value.forEach((item) => {
    item.isFavorite = favoriteIds.includes(item.id.toString())
  })

  if (!parent) {
    parents = menuHierarchy.value
      .filter((item) => !item.parentId && item.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder)
  } else {
    parents = menuHierarchy.value
      .filter((item) => item.parentId === parent.id && item.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder)
    parent.children = parents.length ? parents : null
  }

  for (const parentItem of parents) {
    handleMenuHierarchy(parentItem)
  }

  if (!parent) {
    if (filter.value) {
      for (const item of parents) {
        const filteredMenu = filterMenu(item)
        if (!filteredMenu) {
          parents = parents.filter((p) => p.id !== item.id)
        }
      }
    }

    menuHierarchyDisplay.value = parents.filter((item) => item.isActive).sort((a, b) => a.displayOrder - b.displayOrder)

    if (filter.value) {
      forceExpand.value = true
    }
  }
}

function filterMenu(item: MenuHierarchy): MenuHierarchy | null {
  if (item.children) {
    for (const child of item.children) {
      const filteredMenu = filterMenu(child)
      if (!filteredMenu) {
        item.children = item.children?.filter((c) => c.id !== child.id)
      }
    }
    if (item.children.length) {
      return item
    }
  }

  if (
    t(item.displayNameKey as string)
      .toLowerCase()
      .includes(filter.value.toLowerCase())
  ) {
    return item
  }

  return null
}

function handleFavorite(route: MenuHierarchy) {
  route.isFavorite = !route.isFavorite

  const allFavoritesIds = menuHierarchy.value
    .filter((item) => item.isFavorite)
    .map((item) => item.id)
    .join(",")

  saveSettings("menuFavorites", allFavoritesIds)
  globalStore.setFavorites(allFavoritesIds)
}

function handleFavoriteMenu() {
  const favoriteIds = userPreferences.value.menuFavorites ? userPreferences.value.menuFavorites.split(",") : []
  const favoriteChildren: MenuHierarchy[] = []

  favoriteIds.forEach((id) => {
    const fav = menuHierarchy.value.find((item) => item.id.toString() === id && item.isActive)
    if (fav) {
      fav.isFavorite = true
      favoriteChildren.push(fav)
    }
  })

  favoriteMenuItem.value.children = favoriteChildren.length ? favoriteChildren : null
}

function findReportNode(nodes: any[], path: string): any {
  const segments = path.split("\\").filter(Boolean)
  let current: any[] = nodes
  let found: any = null
  for (const segment of segments) {
    found = current.find((n: any) => n.displayName === segment)
    if (!found) return null
    current = found.children ?? []
  }
  return found
}

function buildReportMenuItems(reportNodes: any[]): MenuHierarchy[] {
  const items: MenuHierarchy[] = []
  for (const report of reportNodes) {
    if (
      report.securityResource &&
      !report.securityResource.split(",").some((r: string) => canAccess(r.trim(), PermissionAction.Read))
    )
      continue

    const hasChildren = report.children && report.children.length > 0
    const newReportItem: MenuHierarchy = {
      id: report.id == 0 ? reportParentCount++ * -1 : report.id * -1,
      displayName: report.displayName as string,
      displayNameKey: "",
      isActive: true,
      isFavorite: false,
      route: hasChildren ? "" : "report-viewer",
      routeParam: hasChildren ? null : JSON.stringify({ id: report.id }),
      children: hasChildren ? buildReportMenuItems(report.children) : null,
      displayOrder: 0,
    }

    // User does not have access to any report under this folder.
    if (hasChildren && newReportItem.children?.length == 0) continue
    menuHierarchy.value.push(newReportItem)
    items.push(newReportItem)
  }
  return items
}

async function handleReportsMenu() {
  // get only items where the routeParam contains "ReportPath"
  const menuWithReports = menuHierarchy.value.filter(
    (item) => item.routeParam?.toLocaleLowerCase().includes("reportpath"),
  )
  if (!menuWithReports.length) return

  const reportList = await getReports()
  const rootMenu = reportList[0]

  if (!rootMenu || !rootMenu.children) return

  menuWithReports.forEach((element: MenuHierarchy) => {
    const routeParam = JSON.parse(element.routeParam as string)
    const reportMenuPath = routeParam.ReportPath as string

    const paths = reportMenuPath
      .split(",")
      .map((p: string) => p.trim())
      .filter(Boolean)

    // Search for reports based on a folder structure
    const allChildren = paths.flatMap((path: string) => {
      const node = findReportNode(reportList, path)

      return node?.children ?? []
    })

    if (allChildren.length) {
      const newReportMenuItem = { ...reportsMenuItem.value }
      newReportMenuItem.children = buildReportMenuItems(allChildren)
      if (newReportMenuItem.children.length) {
        element.children?.push(newReportMenuItem)
      }
    }
  })

  handleFavoriteMenu()
}

// Keeps the favorites scheme (which expects numeric ids) working without exposing the internal Id.
function helpFavoriteId(uniqueId: string): number {
  let hash = 0
  for (let i = 0; i < uniqueId.length; i++) {
    hash = (hash * 31 + uniqueId.charCodeAt(i)) | 0
  }
  return -(1_000_000 + Math.abs(hash))
}

async function handleHelpMenu() {
  const container = menuHierarchy.value.find(
    (item) => !item.parentId && !item.route && item.displayNameKey === "help.menuTitle",
  )
  if (!container) return

  await helpStore.ensureLoaded()
  fetchedHelps = helpStore.helps

  const favoriteIds = userPreferences.value.menuFavorites ? userPreferences.value.menuFavorites.split(",") : []

  const children: MenuHierarchy[] = fetchedHelps
    .filter((h) => h.isActive && h.isMenuDisplayed)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((h) => {
      const id = helpFavoriteId(h.uniqueId)
      return {
        id,
        displayName: getTranslatedLabel(h.title),
        displayNameKey: "",
        isActive: true,
        isFavorite: favoriteIds.includes(id.toString()),
        route: "help-display",
        routeParam: JSON.stringify({ uniqueId: h.uniqueId }),
        displayOrder: h.displayOrder,
        children: null,
      }
    })

  // Remove stale help children from the flat array before re-adding (avoids duplicates on repeated calls).
  const oldHelpIds = new Set((container.children ?? []).map((c) => c.id))
  menuHierarchy.value = menuHierarchy.value.filter((item) => !oldHelpIds.has(item.id))

  if (children.length) {
    container.children = children
    // Add to the flat array so favorite save/lookup works (mirrors how Reports are handled).
    menuHierarchy.value.push(...children)
    handleFavoriteMenu()
    // Re-insert the container into the display if it was previously hidden.
    if (!menuHierarchyDisplay.value.find((item) => item.id === container.id)) {
      menuHierarchyDisplay.value = [...menuHierarchyDisplay.value, container].sort(
        (a, b) => a.displayOrder - b.displayOrder,
      )
    }
  } else {
    // No accessible Help entries: hide the empty container.
    container.children = null
    menuHierarchyDisplay.value = menuHierarchyDisplay.value.filter((item) => item.id !== container.id)
  }
}

function retranslateHelpTitles() {
  if (!fetchedHelps.length) return
  const container = menuHierarchy.value.find(
    (item) => !item.parentId && !item.route && item.displayNameKey === "help.menuTitle",
  )
  container?.children?.forEach((child) => {
    const help = fetchedHelps.find((h) => child.routeParam === JSON.stringify({ uniqueId: h.uniqueId }))
    if (help) child.displayName = getTranslatedLabel(help.title)
  })
}

// Watchers
watch(() => route.fullPath, setActiveExpand, { immediate: true })
watch(
  () => helpStore.helps,
  async () => {
    await handleHelpMenu()
  },
  { deep: true },
)
watch(
  () => userPreferences.value.menuFavorites,
  () => {
    handleFavoriteMenu()
  },
  { immediate: true },
)
watch(
  () => filter.value,
  () => {
    handleMenuHierarchy(null)
    setActiveExpand()
  },
)
watch(
  () => [user.value.language, locale.value],
  () => retranslateHelpTitles(),
)
watch(
  () => user.value.userName,
  async () => {
    // When user changes (in an impersonation session), the entire menu must be refreshed.
    rawMenuHierarchy = await getMenuHierarchy()
    handleMenuPermissions()
    handleReportsMenu()
    handleMenuHierarchy(null)
    await helpStore.reload()
    await handleHelpMenu()
  },
)

// Lifecycle
onMounted(() => {
  getSettings()
  loadMenu()
  loadAppCustomizations()
})
</script>

<style lang="scss" scoped>
.sidebar-logo {
  display: flex;
  justify-content: center;
  padding: 1rem 0;

  img {
    max-width: 50%;
    max-height: 60px;
  }
}
</style>
